import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { delegationService } from '../services/delegationService';
import { VotingDelegation } from '../types/delegation';
import { notifyError } from '../utils/notificationCenter';
import { CheckCircle2, FileText, Info } from 'lucide-react';
import LoadingState from '../components/common/LoadingState';

const AcceptDelegationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [delegation, setDelegation] = useState<VotingDelegation | null>(null);
  
  // Guard para prevenir doble ejecución en React StrictMode
  const hasExecuted = useRef(false);

  const handleAccept = async () => {
    if (!id || !token) {
      const errorMsg = 'Enlace inválido';
      notifyError(errorMsg);
      setError(errorMsg);
      return;
    }

    setLoading(true);

    try {
      const result = await delegationService.acceptDelegation(id, token);
      setDelegation(result);
      setSuccess(true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al aceptar la delegación';
      notifyError(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevenir doble ejecución (React StrictMode ejecuta effects dos veces en desarrollo)
    if (hasExecuted.current) return;
    
    if (id && token) {
      hasExecuted.current = true;
      // Auto-ejecutar la aceptación
      handleAccept();
    } else {
      const errorMsg = 'Enlace inválido o expirado';
      notifyError(errorMsg);
      setError(errorMsg);
    }
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <LoadingState
            layout="inline"
            size="lg"
            message="Aceptando y firmando delegación, generando poder..."
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Error al Aceptar
            </h2>
            <p className="text-red-600 mb-6">
              {error}
            </p>
            <p className="text-sm text-gray-500">
              Verifica que el enlace sea correcto o contacta al delegador.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success && delegation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              <span className="inline-flex items-center justify-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
                <span>Delegación Aceptada</span>
              </span>
            </h2>
            <p className="text-gray-600 mb-6">
              Has aceptado votar por otro propietario en la asamblea.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-green-900 mb-2">
                <span className="inline-flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-700" aria-hidden="true" />
                  <span>Poder Descargado</span>
                </span>
              </h3>
              <div className="text-sm text-green-800 space-y-1">
                <p className="inline-flex items-start gap-2">
                  <CheckCircle2 className="mt-[2px] h-4 w-4 text-green-700" aria-hidden="true" />
                  <span>El documento legal del poder se ha descargado automáticamente.</span>
                </p>
                <p className="text-xs text-green-700 mt-2">
                  Este documento certifica tu aceptación y puede ser usado como constancia legal.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">
                <span className="inline-flex items-center gap-2">
                  <Info className="h-5 w-5 text-yellow-800" aria-hidden="true" />
                  <span>Recuerda</span>
                </span>
              </h4>
              <ul className="text-sm text-yellow-800 space-y-1 text-left">
                <li>• Tu voto valdrá por dos personas</li>
                <li>• El delegador no podrá votar</li>
                <li>• Debes asistir a la asamblea</li>
                <li>• Guarda el poder descargado como constancia</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AcceptDelegationPage;
